import { useWithdrawalRejectToggle } from 'hooks/useWithdrawalRejectToggle';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getGBFeatures } from 'services/growthbook';
import { resetFtdExpSessionFlag } from 'services/growthbook/ftdSession';
import IntegrationCommunication from 'services/integration-communication';
import { sentryCaptureError } from 'services/sentry/capture';
import type { UIPayment, UIPaymentGroups, UIPayout, UIPayoutGroups } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { resetAutoSelectTriggered } from 'store/slices/autoFlow';
import { clearBonuses } from 'store/slices/bonus';
import { skipAutoSelectMethodSelector } from 'store/slices/deposit';
import {
  appChannelSelector,
  lockFlow,
  paymentFlowSelector,
  setGrowthbookInitialized,
  setIsCustomIframeMode,
  setIsStatusPageMode,
  setIsSubmitted,
  setNeedsInitRefetch,
  setUserBlocked,
} from 'store/slices/global';
import { setSelectedCategory } from 'store/slices/methodGrouping';
import { clearNotifications } from 'store/slices/notifications';
import { setRedirectData } from 'store/slices/redirect';
import type { CurrencyBase, CurrencyType } from 'store/slices/wallet';
import {
  onRampCurrenciesSelector,
  setCurrentGroup,
  setPrevCurrentGroupId,
  setPrevSelectedCurrency,
  setPrevSelectedNetworkByCurrency,
  setSelectedCurrency,
  setUserBalance,
} from 'store/slices/wallet';
import { setWalletDataAndTabs } from 'store/thunks/setWalletDataAndTabs';
import { isMethodAvailable } from 'utils/getIsMethodActive';
import { useGetSessionId } from 'utils/session';

import { useIframeReadyMetrics } from '../../hooks/useIframeReadyMetrics';
import { useMetricsRequest } from '../../hooks/useMetrics';
import SkeletonDefaultInit from '../../Modulor/components/Skeleton/init/SkeletonDefaultInit';
import { PaymentType } from '../../Modulor/CurrencySelectContainer/types';
import { useGetPayGroupId } from '../../Modulor/hooks';
import { usePostMessageListener } from '../../services/postMessages';
import { resetContentPageUrl } from '../../store/slices/contentPage';
import { setDefaultPaySessionState } from '../../store/slices/paySessionState';
import { activeTabSelector } from '../../store/slices/tabs';
import { isDepositFlowType, locationListener } from '../../utils';
import type { AppCoreContextProps } from './AppCoreContext';
import { AppCoreContext } from './AppCoreContext';
import { useFlowInit } from './useFlowInit';

interface AppCoreProviderProps {
  children: ReactNode;
}

export const CURRENCY_CHANGE_REASON = {
  CRYPTO_TAB_SWITCH: 'crypto-tab-switch' as const,
  FIAT_TAB_SWITCH: 'fiat-tab-switch' as const,
  ONRAMP_TAB_SWITCH: 'onRamp-tab-switch' as const,
  CURRENCY_SELECT: 'currency-select' as const,
  INITIAL_INIT: 'initial-init' as const,
  MODAL_CLOSE: 'modal-close' as const,
};

type CurrencyChangeReasonKey = keyof typeof CURRENCY_CHANGE_REASON;

const OUTBOUND_CURRENCY_CHANGE_REASON_KEYS: CurrencyChangeReasonKey[] = [
  'CRYPTO_TAB_SWITCH',
  'FIAT_TAB_SWITCH',
  'ONRAMP_TAB_SWITCH',
  'CURRENCY_SELECT',
];

export type CurrencyChangeReason = (typeof CURRENCY_CHANGE_REASON)[keyof typeof CURRENCY_CHANGE_REASON];

const getOutboundCurrencyChangeReason = (reason: CurrencyChangeReason): CurrencyChangeReasonKey | undefined =>
  OUTBOUND_CURRENCY_CHANGE_REASON_KEYS.find((key) => CURRENCY_CHANGE_REASON[key] === reason);

export interface HandleCurrencyProps {
  currency: CurrencyBase;
  isCrypto?: boolean;
  isOnRamp?: boolean;
  tab: CurrencyType;
  currentGroup?: UIPaymentGroups | UIPayoutGroups;
  reason: CurrencyChangeReason;
}

export const AppCoreProvider: FC<AppCoreProviderProps> = ({ children }) => {
  const { userAgent } = navigator;
  const currencyChangeReasonRef = useRef<CurrencyChangeReason | null>(null);
  const isInitialInitDoneRef = useRef(false);
  const isInitialMethodsPageInitializedRef = useRef(false);
  const isSelectGroupRequestDoneRef = useRef<null | boolean>(false);
  const lastEmittedCurrencyChangeRef = useRef<{ currency: string; reason: CurrencyChangeReasonKey } | null>(null);
  const flowType = useAppSelector(paymentFlowSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = useGetSessionId();

  const activeTab = useAppSelector(activeTabSelector);
  const onRampCurrencies = useAppSelector(onRampCurrenciesSelector);
  const isOnRampFlow = activeTab === PaymentType.ON_RAMP;
  const payGroupId = useGetPayGroupId();
  const postMessageHandler = usePostMessageListener(navigate);
  const skipRedirectAfterDeclinePage = useAppSelector(skipAutoSelectMethodSelector);
  const appChannel = useAppSelector(appChannelSelector);

  const isDepositFlow = isDepositFlowType(flowType);
  const hasOnRampCurrency = onRampCurrencies.length > 0;

  const setCurrencyChangeReason = useCallback((reason: CurrencyChangeReason | null) => {
    currencyChangeReasonRef.current = reason;
  }, []);

  const setIsSelectGroupRequestDoneRef = useCallback((value: boolean | null) => {
    isSelectGroupRequestDoneRef.current = value;
  }, []);

  const setIsInitialMethodsPageInitializedRef = useCallback((value: boolean) => {
    isInitialMethodsPageInitializedRef.current = value;
  }, []);

  const setCurrencyState = useCallback(({ currency, tab }: { currency: CurrencyBase; tab: PaymentType }) => {
    dispatch(setSelectedCurrency(currency));
    dispatch(
      setPrevSelectedCurrency({
        tab,
        alpha3: currency.alpha3,
      }),
    );
  }, []);

  /** unified flow init */
  const {
    initialData,
    data,
    isLoading,
    isFetching,
    error,
    getInitByCurrency,
    formatter,
    invalidateFlowCache,
    refetchInit,
    emptyData,
    initDuration,
    hasInitRequest,
  } = useFlowInit({
    flow: flowType,
    payGroupId,
    skipRedirectMethod: skipRedirectAfterDeclinePage,
    userAgent,
    appChannel,
    isOnRampFlow,
    hasOnRampCurrency,
  });

  // Taken in render, not an effect, to match the moment the native widget measures
  const readyAtRef = useRef<number | null>(null);
  if (hasInitRequest && !isLoading && readyAtRef.current === null) {
    readyAtRef.current = performance.now();
  }

  const resetFlowState = useCallback(() => {
    dispatch(clearNotifications());
    dispatch(setCurrentGroup(null));
    dispatch(resetContentPageUrl());
    // TODO: needs refactor ogic for autosubmit if multi-wallet flow
    // dispatch(resetSkippingAutoSelectMethod());
    dispatch(setIsSubmitted(false));
    dispatch(setIsCustomIframeMode(false));
    dispatch(setIsCustomIframeMode(false));
    dispatch(setIsStatusPageMode(false));
    dispatch(setNeedsInitRefetch(false));
    dispatch(setSelectedCategory(null));
    dispatch(setDefaultPaySessionState());
    setCurrencyChangeReason(null);
    setIsSelectGroupRequestDoneRef(null);
    setIsInitialMethodsPageInitializedRef(false);
    dispatch(setUserBlocked(false));
    dispatch(resetAutoSelectTriggered());
    resetFtdExpSessionFlag();
  }, [dispatch, setCurrencyChangeReason, setIsSelectGroupRequestDoneRef, setIsInitialMethodsPageInitializedRef]);

  useMetricsRequest(dispatch);
  useIframeReadyMetrics({
    readyAt: readyAtRef.current,
    initDuration,
  });
  useWithdrawalRejectToggle();

  const handleCurrencyChange = useCallback(
    ({ currency, currentGroup, reason, tab, isCrypto = false, isOnRamp = false }: HandleCurrencyProps) => {
      resetFlowState();
      dispatch(clearBonuses());
      setCurrencyState({
        currency,
        tab,
      });
      const outboundReason = getOutboundCurrencyChangeReason(reason);
      const isDuplicateEmit =
        lastEmittedCurrencyChangeRef.current?.currency === currency.alpha3 &&
        lastEmittedCurrencyChangeRef.current?.reason === outboundReason;

      if (outboundReason && !isDuplicateEmit) {
        lastEmittedCurrencyChangeRef.current = { currency: currency.alpha3, reason: outboundReason };

        try {
          IntegrationCommunication.sendMessage({
            id: 'currencyChanged',
            value: {
              currency: currency.alpha3,
              currencyId: currency.id,
              isFiat: currency.isFiat,
              reason: outboundReason,
              flow: flowType,
            },
          });
        } catch (err) {
          console.warn('Failed to send currencyChanged message', err);
        }
      }
      const { alpha3 } = currency;
      getInitByCurrency(alpha3)
        .unwrap()
        .then((result) => {
          invalidateFlowCache();
          const formatted = formatter(
            result,
            {
              payGroupId,
              isOnRamp,
              hasOnRampCurrency,
              skipRedirectMethod: skipRedirectAfterDeclinePage,
            },
            userAgent,
            appChannel,
          );
          dispatch(setPrevCurrentGroupId(null));
          dispatch(setUserBalance(formatted.userBalance));
          dispatch(setUserBlocked(formatted.userBlocked));

          const methods = formatted.methods || [];
          const firstAvailableMethod = methods.find((m) => isMethodAvailable(m)) || methods[0] || null;
          const networkToSet = currentGroup || (isCrypto ? firstAvailableMethod : null);
          if (networkToSet) {
            dispatch(setCurrentGroup(networkToSet));
            if (isCrypto) {
              dispatch(
                setPrevSelectedNetworkByCurrency({
                  currency: alpha3,
                  network: networkToSet,
                }),
              );
            }
          }
          setCurrencyChangeReason(reason);
        })
        .catch((err) => {
          console.error('Error', err);
        });
    },
    [getInitByCurrency, skipRedirectAfterDeclinePage, payGroupId, appChannel, userAgent, dispatch, flowType],
  );

  useEffect(() => {
    window.addEventListener('message', postMessageHandler);
    return () => window.removeEventListener('message', postMessageHandler);
  }, [postMessageHandler]);

  // Reset autoSelect state and GrowthBook on mount to ensure experiments work
  // when widget is reopened without page reload
  useEffect(() => {
    dispatch(resetAutoSelectTriggered());
    dispatch(setGrowthbookInitialized(false));
    getGBFeatures().reset();
    resetFtdExpSessionFlag();
  }, []);

  useEffect(() => {
    locationListener();
  }, [location]);

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(lockFlow('init'));
    }
  }, [isLoading, isFetching]);

  useEffect(() => {
    if (isLoading || !initialData || isInitialInitDoneRef.current) return;
    dispatch(
      setWalletDataAndTabs({
        cashierSettings: initialData.widgetSettings,
        userBalance: initialData.userBalance,
        defaultFiatCurrency: initialData.defaultFiatCurrency,
        walletCurrencies: initialData.walletCurrencies,
        groups: initialData.methods,
        setCurrencyChangeReason,
        selectedCurrency: initialData.selectedCurrency,
        isDepositFlow,
      }),
    );
    dispatch(setUserBlocked(initialData.userBlocked));
    dispatch(
      setRedirectData({
        merchantUrl: initialData.merchantUrl,
        failUrl: initialData.merchantFailUrl,
        successUrl: initialData.merchantSuccessUrl,
        processingUrl: initialData.merchantProcessingUrl,
        redirectType: initialData.merchantRedirectType,
      }),
    );
    isInitialInitDoneRef.current = true;
    IntegrationCommunication.sendMessage({
      id: 'iframeReady',
    });
  }, [isLoading, handleCurrencyChange, initialData]);

  const contextValue: AppCoreContextProps<UIPayment | UIPayout> = useMemo(
    () => ({
      widgetData: {
        data: isFetching ? emptyData : data,
        isLoading: isFetching,
        refetchInit,
      },
      selectState: {
        setIsSelectGroupRequestDoneRef,
        isSelectGroupRequestDoneRef,
      },
      methodsPageState: {
        isInitialMethodsPageInitializedRef,
        setIsInitialMethodsPageInitializedRef,
      },
      resetFlowState,
      handleCurrencyChange,
      currencyChangeReasonRef,
      setCurrencyChangeReason,
      isInitialInitDoneRef,
    }),
    [data, isFetching, refetchInit, handleCurrencyChange],
  );

  if (isLoading) {
    return <SkeletonDefaultInit showHeader />;
  }

  if (error) {
    sentryCaptureError(`AppCoreProvider error: ${JSON.stringify(error)}`, {
      sessionId,
    });
  }

  return <AppCoreContext.Provider value={contextValue}>{children}</AppCoreContext.Provider>;
};
